import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { formatInteger } from "../helpers/formatNumber";
import { UsersAdminService } from "../api/generated/services/UsersAdminService";
import { AdminUserViewModel } from "../api/generated/models/AdminUserViewModel";
import { ListUsersForAdminSortField } from "../api/generated/models/ListUsersForAdminSortField";
import { formatCopenhagenDateTime } from "../helpers/dateFilters";
import "../api/openApiConfig";
import UserDetailsModal from "../components/AdminUsers/UserDetailsModal";

const PAGE_SIZE = 100;

// URL sort token -> API sort params. "" is the default (email A→Z).
const sortToApi = (
  sort: string
): { sortBy: ListUsersForAdminSortField; sortDescending: boolean } => {
  switch (sort) {
    case "registered_desc":
      return { sortBy: ListUsersForAdminSortField.REGISTERED_AT, sortDescending: true };
    case "registered_asc":
      return { sortBy: ListUsersForAdminSortField.REGISTERED_AT, sortDescending: false };
    default:
      return { sortBy: ListUsersForAdminSortField.EMAIL, sortDescending: false };
  }
};

const stateBadgeClass = (state?: string | null) =>
  state === "Active"
    ? "bg-green-100 text-green-800"
    : state === "Deleted"
    ? "bg-red-100 text-red-800"
    : "bg-gray-100 text-gray-800";

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUserViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState<AdminUserViewModel | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  // URL is the single source of truth for the applied search and page.
  const appliedSearch = searchParams.get("search") ?? "";
  const appliedSort = searchParams.get("sort") ?? "";
  const pageRaw = parseInt(searchParams.get("page") ?? "1", 10);
  const pageNumber = Number.isFinite(pageRaw) && pageRaw >= 1 ? pageRaw : 1;

  const [search, setSearch] = useState(appliedSearch);

  useEffect(() => {
    setSearch(searchParams.get("search") ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const buildParams = (
    searchValue: string,
    page: number,
    sortValue: string
  ): URLSearchParams => {
    const params = new URLSearchParams();
    const trimmed = searchValue.trim();
    if (trimmed) params.set("search", trimmed);
    if (page > 1) params.set("page", String(page));
    if (sortValue) params.set("sort", sortValue);
    return params;
  };

  const handleApplyFilters = () => {
    setSearchParams(buildParams(search, 1, appliedSort));
  };

  const handleResetFilters = () => {
    setSearch("");
    setSearchParams(new URLSearchParams());
  };

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        const response = await UsersAdminService.postApiUsersAdminListUsers({
          search: appliedSearch || undefined,
          pagination: { pageNumber: pageNumber - 1, pageSize: PAGE_SIZE },
          ...sortToApi(appliedSort),
        });
        if (ignore) return;
        setUsers(response.data ?? []);
        setTotal(response.total ?? 0);
        setError(null);
      } catch (err) {
        if (ignore) return;
        setError("Failed to fetch users");
        console.error("Error fetching users:", err);
      } finally {
        if (!ignore) {
          setLoading(false);
          setInitialLoad(false);
        }
      }
    };
    run();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, refreshKey]);

  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="flex flex-col gap-4 mb-10">
        <h2 className="font-medium">Users</h2>
        <form
          className="flex items-end gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleApplyFilters();
          }}
        >
          <div className="w-[360px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search (name, email, phone, ID)
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
            />
          </div>
          <div className="w-[240px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort by
            </label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md bg-white h-[42px]"
              value={appliedSort}
              onChange={(e) =>
                setSearchParams(buildParams(appliedSearch, 1, e.target.value))
              }
            >
              <option value="">Email (A→Z)</option>
              <option value="registered_desc">Registered (newest first)</option>
              <option value="registered_asc">Registered (oldest first)</option>
            </select>
          </div>
          <div className="flex items-center gap-3">
            <Button type="submit">Apply filters</Button>
            <Button type="button" variant="outline" onClick={handleResetFilters}>
              Reset
            </Button>
          </div>
          <div className="bg-white rounded-lg shadow p-4 min-w-[160px] w-fit">
            <div className="text-gray-500 text-sm">Total Users</div>
            <div className="text-2xl font-semibold mt-1">{formatInteger(total)}</div>
          </div>
        </form>
      </div>

      {loading && !initialLoad && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {error && <div className="error mb-4">{error}</div>}

      <div className="overflow-x-auto">
        <table className="table-default">
          <thead>
            <tr>
              <th>Email</th>
              <th>Phone</th>
              <th>Name</th>
              <th className="whitespace-nowrap">Registered</th>
              <th className="whitespace-nowrap">Account State</th>
              <th className="whitespace-nowrap">External ID</th>
              <th className="whitespace-nowrap">OiSTER User ID</th>
              <th>
                <span className="sr-only">Details</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.email || "-"}</td>
                <td className="whitespace-nowrap">{user.phoneNumber || "-"}</td>
                <td>{user.name || "-"}</td>
                <td className="whitespace-nowrap">
                  {formatCopenhagenDateTime(user.registeredAt as any)}
                </td>
                <td>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${stateBadgeClass(
                      user.accountState
                    )}`}
                  >
                    {user.accountState}
                  </span>
                </td>
                <td className="font-mono text-xs whitespace-nowrap">{user.externalId || "-"}</td>
                <td className="whitespace-nowrap">{user.oisterUserId || "-"}</td>
                <td className="whitespace-nowrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedUser(user);
                      setDetailsModalOpen(true);
                    }}
                  >
                    Details
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4">
        <Button
          onClick={() =>
            setSearchParams(
              buildParams(appliedSearch, Math.max(1, pageNumber - 1), appliedSort)
            )
          }
          disabled={pageNumber === 1}
          variant="outline"
        >
          Previous
        </Button>
        <span>
          Page {pageNumber} of {totalPages}
        </span>
        <Button
          onClick={() =>
            setSearchParams(buildParams(appliedSearch, pageNumber + 1, appliedSort))
          }
          disabled={pageNumber >= totalPages}
          variant="outline"
        >
          Next
        </Button>
      </div>
      <UserDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        user={selectedUser}
        onUserChanged={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
};

export default AdminUsers;
